/**
 * Generate a unique block ID
 * @returns {string} A unique block ID in the format "blk_xxxxxxxx"
 */
export function generateBlockId() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = 'blk_';
  for (let i = 0; i < 8; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
}

/**
 * Detect the chart format version
 * @param {string} chartData - The chart data (markdown string or JSON string)
 * @returns {number} 1 for v1 markdown, 2 for v2 JSON
 */
export function detectChartVersion(chartData) {
  if (!chartData) return 1;
  const trimmed = chartData.trim();
  if (trimmed.startsWith('{"version":2') || trimmed.startsWith('{ "version": 2')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed.version === 2) return 2;
    } catch (e) {
      // Not valid JSON, treat as v1
    }
  }
  return 1;
}

/**
 * Parse a V2 chart JSON string
 * @param {string} jsonString - The V2 chart JSON string
 * @returns {Object} Parsed V2 chart object with blocks and layout
 */
export function parseChartV2(jsonString) {
  if (!jsonString) {
    return { version: 2, blocks: [], layout: { portrait: { columns: [] }, landscape: { columns: [] } } };
  }
  try {
    const parsed = JSON.parse(jsonString);
    if (parsed.version !== 2) {
      throw new Error('Invalid V2 chart format');
    }
    return parsed;
  } catch (e) {
    console.error('Error parsing V2 chart:', e);
    return { version: 2, blocks: [], layout: { portrait: { columns: [] }, landscape: { columns: [] } } };
  }
}

/**
 * Serialize V2 chart data to JSON string
 * @param {Array} blocks - Array of V2 block objects
 * @param {Object} layout - Layout object with portrait and landscape columns
 * @returns {string} JSON string
 */
export function serializeChartV2(blocks, layout) {
  return JSON.stringify({
    version: 2,
    blocks,
    layout
  });
}

/**
 * Convert editor state blocks to V2 storage format
 * Simplified V2: every block has header (string) + content (monospace), no type field
 * @param {Array} editBlocks - Array of editor block objects
 * @returns {Object} Object with blocks and layout ready for V2 serialization
 */
export function blocksToV2Format(editBlocks) {
  const blocks = editBlocks.map(block => ({
    id: block.id || generateBlockId(),
    header: block.header || 'Section',
    content: block.content || ''
  }));

  // Create default single-column layout with all block IDs
  const blockIds = blocks.map(b => b.id);
  const layout = {
    portrait: { columns: [blockIds.slice()] },
    landscape: { columns: [blockIds.slice()] }
  };

  return { blocks, layout };
}

/**
 * Convert V2 blocks to editor state format
 * Simplified V2: header is just a string
 * @param {Array} v2Blocks - Array of V2 block objects
 * @returns {Array} Array of editor block objects
 */
export function v2BlocksToEditFormat(v2Blocks) {
  return v2Blocks.map(block => ({
    id: block.id,
    header: block.header || 'Section',
    content: block.content || ''
  }));
}

/**
 * Apply V2 layout to blocks for display
 * @param {Array} blocks - Array of V2 blocks
 * @param {Object} layout - V2 layout object
 * @param {boolean} isLandscape - Whether display is in landscape mode
 * @returns {Array} Array of arrays, each containing block objects
 */
export function applyV2Layout(blocks, layout, isLandscape) {
  const numColumns = isLandscape ? 3 : 2;
  const orientation = isLandscape ? 'landscape' : 'portrait';

  // Create a map of block IDs to blocks for quick lookup
  const blockMap = new Map(blocks.map(b => [b.id, b]));

  const orientationLayout = layout?.[orientation];

  if (!orientationLayout?.columns?.length) {
    // Auto-distribute if no layout
    const columns = Array.from({ length: numColumns }, () => []);
    const blocksPerColumn = Math.ceil(blocks.length / numColumns);

    blocks.forEach((block, index) => {
      const columnIndex = Math.floor(index / blocksPerColumn);
      columns[Math.min(columnIndex, numColumns - 1)].push(block);
    });

    return columns;
  }

  // Apply layout - map IDs to blocks
  const columns = orientationLayout.columns.map(columnIds =>
    columnIds.map(id => blockMap.get(id)).filter(Boolean)
  );

  // Ensure we have the right number of columns
  while (columns.length < numColumns) {
    columns.push([]);
  }

  return columns.slice(0, numColumns);
}

/**
 * Update V2 layout with new column arrangement
 * @param {Object} currentLayout - Current V2 layout
 * @param {Array} columns - Array of arrays with block objects
 * @param {boolean} isLandscape - Whether this is landscape orientation
 * @returns {Object} Updated layout object
 */
export function updateV2Layout(currentLayout, columns, isLandscape) {
  const orientation = isLandscape ? 'landscape' : 'portrait';
  const columnIds = columns.map(col => col.map(block => block.id));

  return {
    ...currentLayout,
    [orientation]: { columns: columnIds }
  };
}

/**
 * Parse markdown content into blocks for chart display
 * @param {string} markdown - The markdown content to parse
 * @returns {Array} Array of block objects with type, title, content, and raw properties
 */
export function parseMarkdownToBlocks(markdown) {
  if (!markdown) return [];

  const lines = markdown.split('\n');
  const blocks = [];
  let currentBlock = null;
  let inCodeBlock = false;
  let codeBlockLines = [];
  let pendingHeader = null;

  const flushCurrentBlock = () => {
    if (currentBlock && currentBlock.lines.length > 0) {
      const block = {
        type: currentBlock.type,
        title: currentBlock.title,
        content: currentBlock.lines.join('\n'),
        raw: currentBlock.raw.join('\n')
      };
      if (pendingHeader) {
        block.header = pendingHeader;
        pendingHeader = null;
      }
      blocks.push(block);
    }
    currentBlock = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Handle code blocks with triple backticks
    if (line.trim().startsWith('```')) {
      if (inCodeBlock) {
        // End of code block
        const block = {
          type: 'code',
          title: null,
          content: codeBlockLines.join('\n'),
          raw: '```\n' + codeBlockLines.join('\n') + '\n```'
        };
        if (pendingHeader) {
          block.header = pendingHeader;
          pendingHeader = null;
        }
        blocks.push(block);
        codeBlockLines = [];
        inCodeBlock = false;
      } else {
        // Start of code block - flush any current block first
        flushCurrentBlock();
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockLines.push(line);
      continue;
    }

    // Handle headers - store as pending to attach to the next content block
    if (line.startsWith('# ') || line.startsWith('## ') || line.startsWith('### ')) {
      flushCurrentBlock();
      const hashCount = line.match(/^#+/)[0].length;
      const headerType = hashCount === 1 ? 'h1' : hashCount === 2 ? 'h2' : 'h3';
      const headerText = line.substring(hashCount + 1);
      pendingHeader = {
        type: headerType,
        title: headerText,
        raw: line
      };
      continue;
    }

    // Handle preformatted text (4+ spaces)
    if (line.match(/^    /)) {
      flushCurrentBlock();
      const block = {
        type: 'pre',
        title: null,
        content: line,
        raw: line
      };
      if (pendingHeader) {
        block.header = pendingHeader;
        pendingHeader = null;
      }
      blocks.push(block);
      continue;
    }

    // Handle empty lines
    if (line.trim() === '') {
      // Empty lines can separate blocks or be part of current block
      if (currentBlock) {
        currentBlock.lines.push('');
        currentBlock.raw.push(line);
      }
      continue;
    }

    // Regular text - add to current block or create new paragraph block
    if (!currentBlock) {
      currentBlock = {
        type: 'paragraph',
        title: null,
        lines: [],
        raw: []
      };
    }
    currentBlock.lines.push(line);
    currentBlock.raw.push(line);
  }

  // Flush any remaining block
  flushCurrentBlock();

  // Handle trailing header with no following content
  if (pendingHeader) {
    blocks.push({
      type: pendingHeader.type,
      title: pendingHeader.title,
      content: pendingHeader.title,
      raw: pendingHeader.raw
    });
    pendingHeader = null;
  }

  // Handle unclosed code block
  if (inCodeBlock && codeBlockLines.length > 0) {
    blocks.push({
      type: 'code',
      title: null,
      content: codeBlockLines.join('\n'),
      raw: '```\n' + codeBlockLines.join('\n')
    });
  }

  return blocks;
}

/**
 * Get a preview of block content for display in rearrange mode
 * @param {Object} block - A block object from parseMarkdownToBlocks
 * @param {number} maxLength - Maximum length of the preview
 * @returns {string} Preview text
 */
export function getBlockPreview(block, maxLength = 50) {
  const content = block.content || '';
  const preview = content.replace(/\n/g, ' ').trim();
  if (preview.length <= maxLength) return preview;
  return preview.substring(0, maxLength) + '...';
}

/**
 * Get the display title for a block (header text or generated title)
 * @param {Object} block - A block object (V1 or V2 format)
 * @param {number} index - Block index for fallback title
 * @returns {string} Display title
 */
export function getBlockTitle(block, index) {
  // V2 simplified format - header is a string
  if (typeof block.header === 'string') return block.header;
  // V1 format - header is an object with title
  if (block.header?.title) return block.header.title;
  if (block.title) return block.title;
  if (block.type === 'code') return 'Code Block';
  if (block.type === 'pre') return 'Preformatted';
  return `Block ${index + 1}`;
}

/**
 * Parse a layout JSON string
 * @param {string} json - JSON string of layout data
 * @returns {Object|null} Parsed layout object or null if invalid
 */
export function parseLayout(json) {
  if (!json) return null;
  try {
    const layout = JSON.parse(json);
    if (layout.version !== 1) return null;
    return layout;
  } catch (e) {
    return null;
  }
}

/**
 * Serialize layout data to JSON string
 * @param {Object} data - Layout data object
 * @returns {string} JSON string
 */
export function serializeLayout(data) {
  return JSON.stringify({
    version: 1,
    ...data
  });
}

/**
 * Distribute blocks into columns automatically
 * @param {Array} blocks - Array of blocks
 * @param {number} numColumns - Number of columns
 * @returns {Array} Array of arrays, each containing block indices
 */
export function autoDistributeBlocks(blocks, numColumns) {
  const columns = Array.from({ length: numColumns }, () => []);
  const blockCount = blocks.length;
  const blocksPerColumn = Math.ceil(blockCount / numColumns);

  blocks.forEach((_, index) => {
    const columnIndex = Math.floor(index / blocksPerColumn);
    columns[Math.min(columnIndex, numColumns - 1)].push(index);
  });

  return columns;
}

/**
 * Apply a layout to blocks, returning columns with block indices
 * Falls back to auto-distribution if layout is invalid or block count changed
 * @param {Array} blocks - Array of blocks
 * @param {Object|null} layout - Layout object
 * @param {boolean} isLandscape - Whether display is in landscape mode
 * @returns {Array} Array of arrays, each containing block indices
 */
export function applyLayout(blocks, layout, isLandscape) {
  const numColumns = isLandscape ? 3 : 2;

  if (!layout) {
    return autoDistributeBlocks(blocks, numColumns);
  }

  const orientation = isLandscape ? 'landscape' : 'portrait';
  const orientationLayout = layout[orientation];

  if (!orientationLayout || !orientationLayout.columns) {
    return autoDistributeBlocks(blocks, numColumns);
  }

  // Validate that all block indices are still valid
  const allIndices = orientationLayout.columns.flat();
  const blockCount = blocks.length;
  const hasInvalidIndices = allIndices.some(idx => idx >= blockCount);
  const hasAllIndices = allIndices.length === blockCount &&
    new Set(allIndices).size === blockCount;

  if (hasInvalidIndices || !hasAllIndices) {
    // Block count changed, fall back to auto-distribution
    return autoDistributeBlocks(blocks, numColumns);
  }

  return orientationLayout.columns;
}
